"use client";

import { useEffect, useState } from "react";
import type { PageContent } from "@/services/api/page-contents";
import ThacSiProgramList from "./ProgramList/ThacSiProgramList";
import { requestJson } from "@/services/api/client";
import { getImageUrl } from "@/utils/api-utils";

interface Program {
  id: string;
  title: string;
  description: string;
  image: string;
  badge: string;
  href: string;
}

interface ThacSiProgramCardsProps {
  content?: PageContent | null;
  programs?: Program[];
}

type ThacSiProgramCardsData = {
  type?: string;
  subtitle?: string;
  title?: string;
};

const isThacSiProgramCardsContent = (value: unknown): value is ThacSiProgramCardsData =>
  !!value && typeof value === "object" && (value as ThacSiProgramCardsData).type === "thac-si-program-cards-section";

type PostCategory = { category: { name: string; slug: string } };
type Post = { id: string; slug: string; title: string; excerpt: string | null; image: string | null; categories: PostCategory[] };
type ApiList<T> = { data: T[] };

export default function ThacSiProgramCards({ content, programs: programsProp }: ThacSiProgramCardsProps) {
  const [fetchedPrograms, setFetchedPrograms] = useState<Program[] | null>(null);

  useEffect(() => {
    if (programsProp && programsProp.length > 0) return;
    const slugs = ["thac-si-dao-tao", "thac-si", "master"];
    (async () => {
      for (const slug of slugs) {
        const res = await requestJson<ApiList<Post> | Post[]>(`/public/posts?categorySlug=${slug}&limit=50&page=1`);
        if (!res.ok || !res.data) continue;
        const posts: Post[] = Array.isArray(res.data) ? res.data : (res.data as ApiList<Post>).data ?? [];
        const filtered = posts.filter((p) =>
          p.categories?.some((c) => {
            const n = c.category.name.toLowerCase();
            const s = c.category.slug.toLowerCase();
            return n.includes("thạc sĩ") || n.includes("thac si") || n === "master" || s.includes("thac-si") || s === "master";
          })
        );
        if (filtered.length > 0) {
          setFetchedPrograms(
            filtered.map((p) => ({
              id: p.id,
              title: p.title,
              description: p.excerpt ?? "",
              image: getImageUrl(p.image),
              badge: p.categories?.[0]?.category?.name ?? "Thạc sĩ",
              href: `/sau-dai-hoc/thac-si/${p.slug}`,
            }))
          );
          return;
        }
      }
      setFetchedPrograms([]);
    })();
  }, [programsProp]);

  const resolved = (() => {
    const raw = content?.content;
    if (!raw) return null;
    if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw);
        return isThacSiProgramCardsContent(parsed) ? parsed : null;
      } catch {
        return null;
      }
    }
    return isThacSiProgramCardsContent(raw) ? raw : null;
  })();

  const subtitle = resolved?.subtitle || "chương trình đào tạo";
  const title = resolved?.title || "CÁC NGÀNH ĐÀO TẠO THẠC SĨ TẠI HUB";
  const programs = (programsProp && programsProp.length > 0) ? programsProp : (fetchedPrograms ?? []);

  return (
    <section className="program-cards-section" aria-label="Các ngành đào tạo Thạc sĩ">
      <div className="container">
        <div className="section-header text-center">
          <p className="section-header__subtitle">{subtitle}</p>
          <h2 className="section-header__title">{title}</h2>
        </div>
        <div id="program-cards">
          {programs.length > 0 ? (
            <ThacSiProgramList programs={programs} />
          ) : fetchedPrograms === null ? (
            <p className="text-center text-muted py-5">Đang tải...</p>
          ) : (
            <p className="text-center text-muted py-5">
              Chưa có bài viết chương trình Thạc sĩ. Vui lòng quày lại sau.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
